import { useMemo, useRef, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import ReactMarkdown from 'react-markdown'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData, useRevalidator, useParams, Link } from 'react-router-dom'
import { getChat, getMessages, updateChatTitle } from '@/api/chats'
import { getProjectExperts } from '@/api/projects'
import { deleteMessage, updateMessage } from '@/api/messages'
import type { ChatLoaderData } from '@/types/project'
import type { ExpertResponse as ExpertResponseType, CollabSection as CollabSectionType } from '@/types/expert'
import { useSSEStream } from '@/hooks/useSSEStream'
import type { SendMessageOptions } from '@/hooks/useSSEStream'
import { useStreamStore } from '@/stores/streamStore'
// NOTE: no Button import — ChatPage renders plain <button> elements; the unused
// import was failing the type gate (TS6133).
import { MessageInput } from '@/components/chat/MessageInput'
import { ExpertResponse } from '@/components/chat/ExpertResponse'
import { SynthesisPanel } from '@/components/chat/SynthesisPanel'
import { StreamingIndicator } from '@/components/chat/StreamingIndicator'
import { CodeBlock } from '@/components/chat/CodeBlock'
import { persistedMessageToExpertResponse } from '@/utils/adaptMessage'
import { buildResponseMarkdown, buildMarkdownFilename, downloadMarkdown } from '@/utils/exportMarkdown'
import { RelayTransparencyPanel } from '@/components/chat/RelayTransparencyPanel'
import { SectionReviewGate } from '@/components/chat/SectionReviewGate'
import { RelayFailedBanner } from '@/components/chat/RelayFailedBanner'
import ExpertErrorCard from '@/components/chat/ExpertErrorCard'
import { getActiveRelay } from '@/api/relay'
import VirtualizedAnswer from '@/components/chat/VirtualizedAnswer'

/**
 * HighlightedText component - highlights search query matches in text.
 * WHY separate component: keeps the highlighting logic isolated and reusable.
 * Case-insensitive matching with yellow highlight on matches.
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'))
  return (
    <>
      {parts.map((part, i) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-glow-amber/30 text-text-primary rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  )
}

/**
 * Collaborative Relay: renders the ordered per-expert sections of a
 * sequentially-relayed answer, each clearly attributed to the expert
 * who wrote it. Shared between the live SSE view (stream.collabSections,
 * growing one section at a time) and a reloaded/persisted message
 * (m.collabSections, all sections present at once) - both pass the same
 * CollabSection[] shape, so one renderer keeps them visually identical.
 */
export function safeRenderContent(content: string, citations?: any[]) {
  if (!content) return null;
  // >4000 chars triggers virtual paging to prevent DOM freeze (Fix #3)
  if (content.length > 4000) return <VirtualizedAnswer content={content} citations={citations} />;
  return <div className="whitespace-pre-wrap break-words text-sm">{content}</div>;
}

function CollabSectionList({ sections }: { sections: CollabSectionType[] }) {
  return (
    <div className="space-y-3">
      {sections.map((section, i) => (
        <div
          key={`${section.expertId}-${i}`}
          className="rounded-lg border-l-4 border-l-brand border-y border-r border-glass-border bg-surface-raised/80 p-4 backdrop-blur-xl"
        >
          <div className="mb-1 flex items-center justify-between">
            <p className="text-xs font-medium text-brand">
              {section.expertName}
              {section.sectionTitle ? ` — ${section.sectionTitle}` : ''}
            </p>
            <button
              type="button"
              onClick={() => {
                const markdown = buildResponseMarkdown({
                  roleName: section.expertName,
                  content: section.content
                } as unknown as ExpertResponseType)
                const filename = buildMarkdownFilename(section.expertName)
                downloadMarkdown(markdown, filename)
              }}
              aria-label={`Download ${section.expertName} section as Markdown`}
              className="text-xs text-text-secondary hover:text-text-primary"
            >
              Download .md
            </button>
          </div>
          <div className="prose prose-invert prose-sm max-w-none text-text-primary">
            <ReactMarkdown components={{ code: CodeBlock }}>{section.content}</ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Source: FRONTEND_SYSTEM_DESIGN.md section 5 (loader pattern) and
 * section 10 (Chat Page wireframe: history + live streaming turn +
 * synthesis + MessageInput at the bottom).
 *
 * PHASE 6 ADDITION - virtualized message history: section 12
 * explicitly calls for "@tanstack/react-virtual for 100+ messages" but
 * no prior phase actually wired it up; this was plain .map() rendering
 * until now. Cross-questioned two real problems before implementing,
 * not just dropped the library in:
 *
 * 1. ExpertResponse items have genuinely variable height (markdown
 *    content length, presence of code blocks, citation lists, warning
 *    banners) - a fixed-size virtualizer (assuming every row is the
 *    same height) would produce wrong scroll positions and visible
 *    gaps/overlaps. Used `measureElement` (dynamic size measurement)
 *    instead of a fixed estimateSize, with a generous initial estimate
 *    (120px) that gets corrected once each row actually renders and
 *    is measured.
 * 2. Auto-scroll-to-bottom on new content must NOT yank the viewport
 *    down if the user has deliberately scrolled UP to read earlier
 *    history while a new SSE chunk streams in - that's a genuinely
 *    hostile UX pattern seen in worse chat UIs. Tracked via
 *    `isNearBottomRef` (updated on scroll), and only auto-scroll when
 *    that ref was true immediately before the update that triggered
 *    it, not unconditionally.
 *
 * The virtualizer only wraps the PERSISTED `messages` list (the part
 * that can genuinely grow to hundreds of items over a long chat's
 * lifetime) - the live streaming turn (pendingUserText, thinking
 * indicator, in-flight ExpertResponses, synthesis, error) renders as
 * normal DOM below the virtualized area, since it's always a small,
 * bounded, ephemeral set of items where virtualization would add
 * complexity for zero benefit.
 */
async function loader({ params, request }: LoaderFunctionArgs) {
  const signal = request.signal
  const projectId = params.projectId as string
  const chatId = params.chatId as string

  const [chat, messages, experts] = await Promise.all([
    getChat(chatId, { signal }),
    getMessages(chatId, { signal }),
    getProjectExperts(projectId, { signal }),
  ])

  return { chat, messages, experts }
}

export const chatRoute = { element: <ChatPage />, loader }

export default function ChatPage() {
  const { chat, messages, experts } = useLoaderData() as ChatLoaderData
  // WHY useParams here even though the loader already has projectId:
  // the loader's params are not exposed to the component via
  // useLoaderData - this is the standard React Router way for a
  // component to read its own route's params independently.
  const { projectId } = useParams<{ projectId: string }>()
  const { sendMessage } = useSSEStream()
  const revalidator = useRevalidator()
  const [pendingUserText, setPendingUserText] = useState<string | null>(null)
  // Last request, kept so a failed answer offers a one-click Retry.
  const [lastSend, setLastSend] = useState<SendMessageOptions | null>(null)
  // Mirrors the composer's choice (same localStorage key) so the answer can be
  // led by the combined synthesis when collaborative is selected. Once a
  // request has actually gone out, lastSend?.answerMode is the ground truth
  // for THIS turn - the user may have flipped the composer's dropdown after
  // sending, and localStorage would then describe the next turn, not this one.
  const answerMode = (() => {
    if (lastSend?.answerMode) return lastSend.answerMode
    try {
      return localStorage.getItem(`chat_${chat.id}_answer_mode`) === 'collaborative'
        ? 'collaborative'
        : 'independent'
    } catch {
      return 'independent'
    }
  })()
  // Assistant-message count when the current request was sent, so the completion
  // effect can tell "history refreshed with the new answer" from "history is old".
  const assistantCountAtSendRef = useRef(0)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(chat.title)
  const [isSavingTitle, setIsSavingTitle] = useState(false)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // Filter messages based on search query
  // WHY case-insensitive: users expect "API" to match "api" and vice versa
  // WHY trim: ignore leading/trailing whitespace in search
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages
    const q = searchQuery.toLowerCase().trim()
    return messages.filter((m) => m.content.toLowerCase().includes(q))
  }, [messages, searchQuery])

  async function handleSaveTitle() {
    const trimmed = titleDraft.trim()
    if (!trimmed || trimmed === chat.title) { setIsEditingTitle(false); return }
    setIsSavingTitle(true)
    try {
      await updateChatTitle(chat.id, trimmed)
      await revalidator.revalidate()
    } catch (err) {
      console.error('rename chat failed', err)
    } finally {
      setIsSavingTitle(false)
      setIsEditingTitle(false)
    }
  }

  async function handleDeleteMessage(messageId: string) {
    if (!window.confirm('Delete this message?')) return
    setDeletingMessageId(messageId)
    try {
      await deleteMessage(messageId)
      await revalidator.revalidate()
    } catch (err) {
      console.error('delete message failed', err)
    } finally {
      setDeletingMessageId(null)
    }
  }

  async function handleSaveEdit(messageId: string) {
    if (!editingContent.trim()) return
    try {
      await updateMessage(messageId, editingContent.trim())
      await revalidator.revalidate()
      setEditingMessageId(null)
    } catch (err) {
      console.error('update message failed', err)
    }
  }

  const stream = useStreamStore((s) => s.activeStreams.get(chat.id))
  const clearStream = useStreamStore((s) => s.clearStream)

  const isStreamActive = stream !== undefined && stream.status !== 'complete' && stream.status !== 'error'

  // A hydrated relay (reconnect/reload) must render its gate/failure/transcript
  // even though `answerMode` resets to 'independent' on a fresh page load. This
  // derived flag is the single source of truth for "a relay exists to show".
  const relayActive = answerMode === 'collaborative' || Boolean(stream?.relaySteps?.length || stream?.pendingReview || stream?.relayFailure)

  // Once the persisted history contains an assistant reply, the streamed copy is
  // redundant: drop it so the answer is not rendered twice. If the refetch never
  // brings it, the streamed copy stays visible instead of the answer vanishing.
  useEffect(() => {
    if (stream?.status !== 'complete') return
    // Clear only when the history actually GREW — comparing to the count at send
    // time, because the chat already has older assistant turns and a plain
    // "has any assistant message" check would wipe the new answer instantly.
    const assistantNow = messages.filter((m) => m.role === 'assistant').length
    if (assistantNow > assistantCountAtSendRef.current) clearStream(chat.id)
  }, [messages, stream?.status, chat.id, clearStream])

  // Reconnect/reload hydration (Finding #5): the backend persists every relay
  // step plus the pending-review / failure state, but the live SSE stream is gone
  // after a reload. On mount, pull the active relay and rehydrate the store so the
  // review gate / retry banner / transparency panel reappear instead of the user
  // believing the work was lost. Never clobber a live in-flight stream.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const relay = await getActiveRelay(chat.id)
        if (cancelled || !relay) return
        const existing = useStreamStore.getState().activeStreams.get(chat.id)
        if (existing && existing.status !== 'complete' && existing.status !== 'error') return
        if (!existing) useStreamStore.getState().startStream(chat.id)
        useStreamStore.getState().appendChunk(chat.id, {
          status: 'complete',
          relaySteps: relay.events ?? [],
          pendingReview: relay.pendingReview ?? null,
          relayFailure: relay.failure ?? null,
        })
      } catch {
        // 404 (no active run) or a transient network error — nothing to hydrate.
      }
    })()
    return () => { cancelled = true }
  }, [chat.id])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)
  // Feature #22: Track scroll position to show/hide "Scroll to Bottom" button
  const [showScrollButton, setShowScrollButton] = useState(false)

  const virtualizer = useVirtualizer({
    count: filteredMessages.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 120,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 5,
  })

  // Track whether the user is scrolled near the bottom, so streaming
  // updates only auto-scroll when that was already true - see header
  // comment for why unconditional auto-scroll is a real UX hazard here.
  // Feature #22: Also update showScrollButton state based on scroll position
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    function handleScroll() {
      const threshold = 80 // px - "close enough to bottom" tolerance
      const isNearBottom = el!.scrollHeight - el!.scrollTop - el!.clientHeight < threshold
      isNearBottomRef.current = isNearBottom
      // Feature #22: Show button when user scrolls up (not near bottom)
      // WHY threshold: only show button when meaningfully scrolled up,
      // not for tiny scroll jitter at the bottom
      setShowScrollButton(!isNearBottom)
    }
    el.addEventListener('scroll', handleScroll)
    // Initial check on mount
    handleScroll()
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isNearBottomRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
    // Re-run whenever the streaming turn changes shape (new expert
    // response arrives, synthesis appears, etc.) or history length
    // changes (revalidated after completion) - both are moments new
    // content might now be below the fold.
  }, [messages.length, stream?.expertResponses.length, stream?.synthesis, pendingUserText])

  // Feature #22: Scroll to bottom handler
  // WHY smooth scroll: instant jump is jarring, smooth scroll is more natural
  // WHY also update isNearBottomRef: prevents button from briefly reappearing
  // after scroll completes (the scroll event fires async)
  function scrollToBottom() {
    const el = scrollContainerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    isNearBottomRef.current = true
    setShowScrollButton(false)
  }

  async function handleSend(
    text: string,
    expertIds: string[],
    files?: File[],
    replyToMessageId?: string,
    includeFullThread?: boolean,
    templateName?: string,
    genericAllowancePct?: number,
    answerMode?: 'independent' | 'collaborative'
  ) {
    // Remember exactly what was sent so a failed answer can be retried with one
    // click instead of the user retyping the whole question (or re-attaching the
    // same files).
    const payload: SendMessageOptions = {
      chatId: chat.id,
      message: text,
      expertIds,
      files,
      replyToMessageId,
      includeFullThread,
      templateName,
      genericAllowancePct,
      answerMode,
    }
    setLastSend(payload)
    assistantCountAtSendRef.current = messages.filter((m) => m.role === 'assistant').length
    setPendingUserText(text)
    await sendMessage(payload)

    // WHY re-read stream status via getState() rather than the `stream`
    // closure variable: sendMessage's promise resolves after the SSE
    // loop returns, but this handler function's own closure captured
    // `stream` from BEFORE sendMessage ran - by the time we get here,
    // the store has almost certainly already moved past that stale
    // snapshot. Reading fresh state avoids acting on outdated status.
    const finalStatus = useStreamStore.getState().activeStreams.get(chat.id)?.status
    if (finalStatus === 'complete') {
      // Keep the streamed answer on screen until the PERSISTED messages actually
      // contain it — a matching effect below clears the stream at that point.
      // Previously the stream was cleared immediately after revalidate(), so
      // when the refetch came back without the new assistant row (or failed) the
      // answer vanished and the screen was left empty.
      await revalidator.revalidate()
      setPendingUserText(null)
    }
    // WHY no action on 'error': the error message stays visible via
    // StreamingIndicator/stream.error until the user tries again or
    // navigates away - clearing it immediately would hide a failure
    // the user needs to see and act on.
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b border-surface-border px-6 py-4">
        <Link
          to={`/projects/${projectId}`}
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          {'←'} Back to Project
        </Link>
        {isEditingTitle ? (
          <input
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSaveTitle() }
              if (e.key === 'Escape') { setIsEditingTitle(false); setTitleDraft(chat.title) }
            }}
            disabled={isSavingTitle}
            className="flex-1 rounded-md border border-brand/40 bg-surface-overlay px-2 py-1 text-lg font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-50"
          />
        ) : (
          <div className="group flex flex-1 items-center gap-1.5">
            <h2
              className="cursor-pointer text-lg font-medium hover:text-brand"
              onDoubleClick={() => { setTitleDraft(chat.title); setIsEditingTitle(true) }}
              title="Double-click to rename"
            >
              {chat.title}
            </h2>
            <button
              onClick={() => { setTitleDraft(chat.title); setIsEditingTitle(true) }}
              className="opacity-0 transition-opacity group-hover:opacity-100 text-text-disabled hover:text-text-primary"
              title="Rename chat"
              aria-label="Rename chat"
            >
              {'✏️'}
            </button>
          </div>
        )}
        {showSearch ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setShowSearch(false); setSearchQuery('') }
              }}
              className="w-64 rounded-md border border-surface-border bg-surface-overlay px-3 py-1.5 text-sm text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
            <button
              onClick={() => { setShowSearch(false); setSearchQuery('') }}
              className="text-xs text-text-secondary hover:text-text-primary"
              title="Close search"
            >{'✕'}</button>
          </div>
        ) : (
          <button
            onClick={() => setShowSearch(true)}
            className="flex items-center gap-1.5 rounded-md border border-surface-border bg-surface-overlay px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:border-brand/40 transition-colors"
            title="Search in chat"
          >
            <span>{'🔍'}</span>
            <span>Search</span>
          </button>
        )}
        {searchQuery && (
          <span className="text-xs text-text-disabled">
            {filteredMessages.length} result{filteredMessages.length !== 1 ? 's' : ''}
            {filteredMessages.length === 0 && ' - try different keywords'}
          </span>
        )}
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6 relative">
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const m = filteredMessages[virtualRow.index]!
            return (
              <div
                key={m.id}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                className="pb-3"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {m.role === 'user' ? (
                  <div className="group ml-auto max-w-[80%]">
                    {editingMessageId === m.id ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          className="w-full rounded-md bg-brand/10 p-3 text-sm text-text-primary resize-none border border-brand/30 focus:outline-none focus:border-brand"
                          rows={3}
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(m.id) }
                            if (e.key === 'Escape') setEditingMessageId(null)
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditingMessageId(null)} className="text-xs text-text-secondary hover:text-text-primary px-2 py-1">Cancel</button>
                          <button onClick={() => handleSaveEdit(m.id)} className="text-xs bg-brand/20 hover:bg-brand/30 text-text-primary px-3 py-1 rounded">Save</button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className={`rounded-md bg-brand/10 p-3 text-sm text-text-primary ${
                          searchQuery && m.content.toLowerCase().includes(searchQuery.toLowerCase())
                            ? 'ring-2 ring-glow-amber/50 bg-glow-amber/5'
                            : ''
                        }`}>
                          {searchQuery && m.content.toLowerCase().includes(searchQuery.toLowerCase()) ? (
                            <HighlightedText text={m.content} query={searchQuery} />
                          ) : (
                            m.content
                          )}
                        </div>
                        <div className="absolute -top-6 right-0 flex gap-1">
                          <button
                            onClick={() => { setEditingMessageId(m.id); setEditingContent(m.content) }}
                            className="text-xs text-text-secondary hover:text-text-primary bg-surface-raised border border-surface-border rounded px-2 py-0.5"
                          >Edit</button>
                          <button
                            onClick={() => handleDeleteMessage(m.id)}
                            disabled={deletingMessageId === m.id}
                            className="text-xs text-mode-refuse hover:text-mode-refuse/80 bg-surface-raised border border-surface-border rounded px-2 py-0.5 disabled:opacity-50"
                          >{deletingMessageId === m.id ? '...' : 'Delete'}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  (() => {
                    // Collaborative Relay: a merged message has no single
                    // expertId (it represents 2+ experts), so
                    // persistedMessageToExpertResponse always returns null
                    // for it below - check collabSections FIRST so a reload
                    // renders the same attributed, sectioned view the live
                    // stream showed, instead of falling into the generic
                    // plain-text fallback.
                    if (m.collabSections && m.collabSections.length > 0) {
                      return <CollabSectionList sections={m.collabSections} />
                    }
                    const asExpertResponse = persistedMessageToExpertResponse(m, experts)
                    if (!asExpertResponse) {
                      // Shouldn't happen for a well-formed assistant row, but
                      // a malformed one (missing expertId) degrades to plain
                      // text rather than being silently dropped.
                      return (
                        <div className="rounded-md border border-surface-border p-3 text-sm">
                          {m.content}
                        </div>
                      )
                    }
                    return <ExpertResponse response={asExpertResponse} persistedMessageId={m.id} chatId={chat.id} />
                  })()
                )}
              </div>
            )
          })}
        </div>

        {/* Live streaming turn - deliberately NOT virtualized, see header comment. */}
        <div className="mt-3 space-y-3">
          {pendingUserText && (
            <div className="ml-auto max-w-[80%] rounded-md bg-brand/10 p-3 text-sm text-text-primary">
              {pendingUserText}
            </div>
          )}

          {stream && (isStreamActive || relayActive) && (
            <>
              {/* Show thinking indicator only when no content has arrived yet */}
              {stream.status === 'thinking' && !stream.streamingContent && (
                <StreamingIndicator stream={stream} expertCount={experts.length} />
              )}

              {/* Live streaming content — shown as tokens arrive via chunk events.
                  Replaced by ExpertResponse once complete event fires. */}
              {stream.streamingContent && (
                <div className="rounded-lg border-l-4 border-l-mode-advise border-y border-r border-glass-border bg-surface-raised/80 p-4 backdrop-blur-xl">
                  <div className="prose prose-invert prose-sm max-w-none text-text-primary">
                    <ReactMarkdown components={{ code: CodeBlock }}>
                      {stream.streamingContent}
                    </ReactMarkdown>
                  </div>
                  <p className="mt-2 text-xs text-text-disabled animate-pulse">Streaming...</p>
                </div>
              )}

              {answerMode === 'collaborative' && stream?.synthesis && (
                <SynthesisPanel synthesis={stream.synthesis} onDecide={(instruction) => void handleSend(instruction, lastSend?.expertIds ?? [])} />
              )}

              {relayActive && stream.relaySteps && stream.relaySteps.length > 0 && (
                <RelayTransparencyPanel steps={stream.relaySteps} />
              )}

              {/* Collaborative Relay: sections arrive one at a time via
                  collab_section SSE events, in relay order - render them as
                  they land instead of waiting for the whole turn to finish. */}
              {stream.collabSections.length > 0 ? (
                <CollabSectionList sections={stream.collabSections} />
              ) : (
                answerMode === 'independent' && stream.expertResponses.map((partial, i) =>
                  partial.expertId ? (
                    <ExpertResponse key={partial.expertId} response={partial as ExpertResponseType} isStreaming />
                  ) : (
                    <p key={i} className="text-xs text-text-disabled">Malformed response - missing expertId</p>
                  )
                )
              )}

              {relayActive && stream.pendingReview && (
                <SectionReviewGate
                  review={stream.pendingReview}
                  onDone={() => {
                    useStreamStore.getState().appendChunk(chat.id, { pendingReview: null })
                  }}
                />
              )}

              {relayActive && stream.relayFailure && (
                <RelayFailedBanner
                  failure={stream.relayFailure}
                  onRetry={() => {
                    clearStream(chat.id)
                    void sendMessage({ ...lastSend!, resumeRunId: stream.relayFailure!.runId })
                  }}
                />
              )}
            </>
          )}

          {/* Independent keeps the original order (experts, then synthesis). */}
          {answerMode === 'independent' && stream?.synthesis && (
            <SynthesisPanel synthesis={stream.synthesis} onDecide={(instruction) => void handleSend(instruction, lastSend?.expertIds ?? [])} />
          )}

          {stream?.status === 'error' && (
            <ExpertErrorCard
              message={stream.error || "Unknown error"}
              code={(stream.error?.split(":")[0] || "").slice(0,60)}
              onRetry={lastSend ? () => { clearStream(chat.id); void sendMessage(lastSend); } : undefined}
            />
          )}
        </div>

        {/* Feature #22: Floating "Scroll to Bottom" button
            WHY bottom-right: standard chat UI pattern (WhatsApp, Telegram, Slack)
            WHY only when scrolled up: no need when already at bottom
            WHY fixed positioning: stays visible even when scrolling
            WHY z-10: above chat content but below modals */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-24 right-8 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-brand/40 bg-surface-raised shadow-lg transition-all duration-200 hover:scale-110 hover:border-brand hover:bg-brand/10 hover:shadow-xl"
            title="Scroll to bottom"
            aria-label="Scroll to bottom"
          >
            <span className="text-lg">⬇</span>
          </button>
        )}
      </div>

      <MessageInput chatId={chat.id} experts={experts} onSend={handleSend} isSending={isStreamActive} />
    </div>
  )
}
